import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TestIdDirective } from '@design/core/test-id.directive';
import { DomainMigrationService } from '@domain/domain-migration.service';
import { FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { HelpLinkType, HelpLinksService } from '@app/help-links/help-links.service';

/**
 * The migration date as both cards word it. Still separate from the modal's
 * DOMAIN_MIGRATION_DATE_LABEL, which spells the year out too ("19th October 2026").
 */
export const DOMAIN_MIGRATION_NOTICE_DATE_LABEL = '19th October';

/** Link text on both cards, matching the modal's help link. */
export const DOMAIN_MIGRATION_HELP_LINK_LABEL = 'Learn more';

/** Which of the two messages the card is showing, if any. */
export type DomainMigrationNoticeState = 'none' | 'pre-move' | 'post-move';

/**
 * Pre-authentication counterpart to DomainMigrationModalComponent, in two halves:
 *
 * - on the retiring domain, warns that saved logins, bookmarks and passkeys won't carry over;
 * - on the replacement domain, reassures visitors they are in the right place and nudges anyone
 *   who hasn't updated those details yet.
 *
 * Each half has its own flag, separate again from the modal's, so all three can be switched
 * independently. The domain the browser is actually on decides which half is eligible; its flag
 * then decides whether it shows. Unlike the pre-move half, the post-move message does not
 * self-expire: everyone is on the new domain permanently, so it runs until its flag is switched
 * off in ConfigCat.
 */
@Component({
  selector: 'app-domain-migration-notice',
  templateUrl: './domain-migration-notice.component.html',
  styleUrl: './domain-migration-notice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TestIdDirective]
})
export class DomainMigrationNoticeComponent {
  private readonly domainMigrationService = inject(DomainMigrationService);
  private readonly featureFlags = inject(FeatureFlagsServiceInjectionToken);
  private readonly helpLinks = inject(HelpLinksService);

  /**
   * Read straight from getValue$, which resolves off ConfigCat's first *anonymous* fetch.
   *
   * Neither is gated on userContextLoaded$ (unlike showDomainMigrationModalIfPending, which shows the
   * post-login modal): that only emits once an active membership resolves after sign-in, so on the
   * sign-in page it never fires and the notice would never appear. For the same reason the flag
   * must stay plain global on/offs in ConfigCat -- pre-authentication the user context carries no
   * identifier or email, so targeting rules and percentage rollouts don't evaluate.
   */
  private readonly cintraCloudNoticeEnabled = toSignal(this.featureFlags.getValue$('showDomainMigrationCintraCloudNotice', false), {
    initialValue: false
  });
  private readonly cintraPeopleNoticeEnabled = toSignal(this.featureFlags.getValue$('showDomainMigrationCintraPeopleNotice', false), {
    initialValue: false
  });

  private readonly onOldDomain = this.domainMigrationService.isOnOldDomain();
  private readonly onNewDomain = this.domainMigrationService.isOnNewDomain();

  /**
   * The old domain wins when a host matches both, which is the case in dev, e2e and CI -- they
   * configure one hostname as both `oldDomain` and `newDomain`. In prod and test the two are
   * distinct, so exactly one branch matches and the precedence never comes into play.
   */
  protected readonly state = computed<DomainMigrationNoticeState>(() => {
    if (this.onOldDomain) {
      return this.cintraCloudNoticeEnabled() ? 'pre-move' : 'none';
    }
    if (this.onNewDomain) {
      return this.cintraPeopleNoticeEnabled() ? 'post-move' : 'none';
    }
    return 'none';
  });

  protected readonly oldDomainLabel = this.domainMigrationService.oldDomainLabel;
  protected readonly newDomainLabel = this.domainMigrationService.newDomainLabel;
  protected readonly dateLabel = DOMAIN_MIGRATION_NOTICE_DATE_LABEL;
  /**
   * The audience-neutral article: this card renders on the sign-in page, where no active user
   * exists yet for getUrl() to branch on. Its post-login counterpart, the migration modal, does
   * branch, so an integrated or payroll-only reader gets the article written for them.
   */
  protected readonly helpUrl = this.helpLinks.getAnonymousUrl(HelpLinkType.DomainMigration);
  protected readonly helpLinkLabel = DOMAIN_MIGRATION_HELP_LINK_LABEL;
}
